import {
    SimplePresentation,
    SimplePresentationRunnerController
} from "../../../core/winpresentation/SimplePresentationRunner";
import {gsap, TimelineMax} from "gsap";
import {TheBorder} from "../../TheBorder";
import {TheBorderXSplitSymbol} from "../../reels/TheBorderXSplitSymbol";
import {ISymbol} from "../../../core/reel/ReelInterfaces";
import {TheBorderSound} from "../../sound/TheBorderSound";
import {TheBorderXNudgeView} from "../../view/TheBorderXNudgeView";
import {TheBorderScatterSymbol} from "../../reels/TheBorderScatterSymbol";

/**
 * Created by Jerker Nord on 2022-04-05.
 */

export class TheBorderXSplitWinRunner implements SimplePresentation {

    private _mainTL:TimelineMax;
    private _counter:number;
    private _controller:SimplePresentationRunnerController;
    private _xSplitPositions:PIXI.Point[];
    private _xSplit:boolean;
    private _timeScale:number;

    public shouldRun():boolean {
        this._xSplitPositions = this.parseXSplitPositions(TheBorderXNudgeView.getReelsData());
        return this._xSplit;
    }

    public start(controller:SimplePresentationRunnerController):void {
        this._controller = controller;
        this._timeScale = 1;
        this._mainTL = gsap.timeline();
        this._mainTL.add(() => this._controller.setSkipCallback(() => this.skip()));
        this._counter = -1;
        this.doXSplit();
        if(this._mainTL.duration() == 0) {
            this._mainTL.add(gsap.to(this, {duration : 0.001}));
        }
    }

    private parseXSplitPositions(reels:string[][]):PIXI.Point[] {
        const xSplitPositions:PIXI.Point[] = [];
        this._xSplit = false;
        for(let i:number = 0; i < reels.length; i++) {
            for(let j:number = 0; j < reels[i].length; j++) {
                if(reels[i][j] === "X_SPLIT" || reels[i][j] === "X_SPLIT_H") {
                    this._xSplit = true;
                    let xSplitPosition:PIXI.Point = new PIXI.Point(i, j);
                    xSplitPositions.push(xSplitPosition);
                }
            }
        }
        return xSplitPositions;
    }

    private doXSplit():void {
        this._counter++;
        if(this._counter == this._xSplitPositions.length) {
            this._controller.finish();
        } else {
            this.playXSplitAnimation(this._xSplitPositions[this._counter].x, this._xSplitPositions[this._counter].y);
        }
    }

    private playXSplitAnimation(xSplitReelPosition:number, splitRowId:number):void {
        const xSplitAnimationTL:gsap.core.Timeline = gsap.timeline({onComplete : () => this.doXSplit()}),
            xSplitSymbol:TheBorderXSplitSymbol = TheBorder.xSplitSymbols.getXSplitSymbolOnReelArea(xSplitReelPosition, splitRowId);
        let splitOffset:number = (11 / 30);
        xSplitAnimationTL.timeScale(this._timeScale);

        //Play win animation on X_Split symbol
        xSplitAnimationTL.add([
            xSplitSymbol.getWinAnimation(),
            () => {
                if(this._timeScale == 1) {
                    TheBorder.soundPlayer.playSound(TheBorderSound.SPLIT_SOUNDS[Math.floor(Math.random() * TheBorderSound.SPLIT_SOUNDS.length)]); //#50
                    TheBorder.soundPlayer.playXSplitSymbolCutSound(); //#210
                }
            }
        ], 0);

        //Split all symbols to the left of XSplit symbol
        for(let j:number = xSplitReelPosition - 1; j > -1; j--) {
            //1. Split of full symbol, not offset
            //2. Split of two half symbols, not offset (= symbol multiplier X2)
            //3. Split of two full symbols, offset (= 4 half symbols)
            //4. Split of one full (lower part) and one half symbol, offset (= full split into two, half multiplier X2)
            //5. Split of one full (upper part) and one half symbol, offset (= full split into two, half multiplier X2)

            //Symbol(s) to the left
            let symbolsToTheLeft:ISymbol[] = TheBorder.allReelsView.mainReelModels[j].reelView.getSymbolsClosestToYPos(xSplitSymbol.position.y, (xSplitSymbol.sizeMultiplier === 1 ? 100 : 50));
            for(let i:number = 0; i < symbolsToTheLeft.length; i++) {
                const sizeMultiplier:number = (symbolsToTheLeft[i].sizeMultiplier);
                if((sizeMultiplier === 1) || (sizeMultiplier === 0.5 && (i === 0 || i === 1))) {
                    xSplitAnimationTL.add(TheBorder.xSplitView.playXSplitOnOtherSymbols(symbolsToTheLeft[i].position, j, (sizeMultiplier === 0.5)), (splitOffset));
                }

                //Split into two symbols if needed
                if(sizeMultiplier === 1) {
                    xSplitAnimationTL.add(() => {
                        TheBorder.allReelsView.mainReelModels[j].reelView.splitSymbolIntoTwoHalf(symbolsToTheLeft[i]);

                        if(symbolsToTheLeft[i].symbolName.indexOf("BN") > -1) {
                            this.checkForScatterSound();
                        }
                    }, (splitOffset) + (2 / 30));
                } else {
                    xSplitAnimationTL.add(() => {
                        symbolsToTheLeft[i].splitLevel *= 2;

                        if(symbolsToTheLeft[i].symbolName.indexOf("BN") > -1) {
                            this.checkForScatterSound();
                        }
                    }, (splitOffset) + (2 / 30));
                }
            }
            splitOffset += (2 / 30);
        }
        xSplitAnimationTL.add(() => {
            TheBorder.xSplitView.removeAll();
        });
    }

    private checkForScatterSound():void {
        const allScatterSymbols:TheBorderScatterSymbol[] = TheBorder.scatterSymbols.getAllUsedSymbols();
        let scatterCount:number = 0;
        allScatterSymbols.forEach((sym:TheBorderScatterSymbol) => {
            scatterCount += sym.splitLevel;
        });

        let soundName:string = "";
        if(scatterCount === 6) {
            soundName = TheBorderSound.SCATTER_UNLOCK_FS;
        } else if(scatterCount === 7) {
            soundName = TheBorderSound.SCATTER_UNLOCK_SFS;
            
            TheBorder.soundPlayer.fadeOutEffect({
                name: TheBorderSound.SCATTER_UNLOCK_FS,
                duration: 50,
                to: 0
            });
        }
        if(soundName) {
            TheBorder.soundPlayer.playSound(soundName);
        }
    }

    private skip():void {
        this._timeScale = 10;
    }
}